"use client";

import { Check, Link2 } from "lucide-react";
import { type CSSProperties, useRef } from "react";

import { DISPLAY } from "@/components/editorial";
import { centre, Cursor, Decision, enter, FRAME_CORNERS, LoopStage, Marked, Panel, PILL, SourceLabel, SourceMark, type SourceId, UiRoot, ui, useAnchors, useTicker } from "@/components/moments/ui";
import { useTimeline } from "@/components/moments/use-timeline";

/**
 * The sample workspace's first proposal (fixture-proposals.ts): Xero's
 * Tarnwick University and HubSpot's University of Tarnwick, their names
 * 84% alike by the trigram comparator, their website domains identical,
 * 90% confidence overall.
 */
type SourceRecord = { source: SourceId; kind: string; name: string; fields: readonly (readonly [string, string])[] };

const XERO: SourceRecord = {
  source: "xero",
  kind: "contact",
  name: "Tarnwick University",
  fields: [
    ["Account", "TARN01"],
    ["Contact", "Rhian Castell"],
    ["Website", "tarnwick.example"],
  ],
};

const HUBSPOT: SourceRecord = {
  source: "hubspot",
  kind: "company",
  name: "University of Tarnwick",
  fields: [
    ["City", "Tarnwick"],
    ["Industry", "Higher education"],
    ["Domain", "tarnwick.example"],
  ],
};

/** The two names the proposal matches, and its confidence, for a study that draws them another way. */
export const MATCH_NAMES = { xero: XERO.name, hubspot: HUBSPOT.name } as const;
export const MATCH_CONFIDENCE = 90;

/** The linked customer: one record, both sources' facts. */
const LINKED_FIELDS = [
  ["Account", "TARN01"],
  ["Contact", "Rhian Castell"],
  ["Industry", "Higher education"],
  ["Website", "tarnwick.example"],
] as const;

const NAME_SCORE = 84;

/** What the motion shows, for a reader who cannot watch it. */
export const MATCH_SUMMARY = `An illustration of a proposal in the Inbox. Xero's contact ${XERO.name} and HubSpot's company ${HUBSPOT.name} look like the same customer: their names are ${NAME_SCORE}% alike and their website domains are identical, at ${MATCH_CONFIDENCE}% confidence. A person accepts the match, and the two records become one linked customer.`;

/* Steps: 0 rest, 1 the records rise, 2 the names connect, 3 the domains
 * connect, 4 the confidence fills, 5 the actions rise and the pointer
 * arrives, 6 Accept, 7 the records converge, 8 one linked customer, 9 out. */
const STEPS = [500, 800, 850, 750, 1000, 1150, 450, 700, 3300, 450] as const;
const CARDS = 1;
const NAMES = 2;
const DOMAIN = 3;
const SCORE = 4;
const ACTIONS = 5;
const PRESS = 6;
const CONVERGE = 7;
const LINKED = 8;
const OUT = 9;

function RecordCard({ record, side, marks }: { record: SourceRecord; side: "left" | "right"; marks: { name: boolean; site: boolean } }) {
  const last = record.fields.length - 1;
  return (
    <Panel anchor={side} className="p-5">
      <SourceLabel source={record.source} kind={record.kind} />
      <p data-anchor={`${side}-name`} style={DISPLAY} className="mt-2.5 w-fit text-[19px] leading-tight font-semibold tracking-[-0.015em]">
        <Marked on={marks.name}>{record.name}</Marked>
      </p>
      <dl className="mt-4 border-t border-[var(--ui-line)] pt-1">
        {record.fields.map(([label, value], index) => (
          <div key={label} data-anchor={index === last ? `${side}-site` : undefined} className="flex justify-between gap-4 py-2 text-[13px]">
            <dt className="text-[var(--ui-quiet)]">{label}</dt>
            <dd className="font-medium">{index === last ? <Marked on={marks.site}>{value}</Marked> : value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/** Evidence as the Inbox names it: a signal and its strength. */
function EvidenceChip({ label, value, check = false, style }: { label: string; value: string; check?: boolean; style?: CSSProperties }) {
  return (
    <span {...FRAME_CORNERS} className={`${ui.panel} ${ui.pop} flex items-center gap-1.5 px-3 py-1.5 text-[12px] whitespace-nowrap`} style={style}>
      <span className="text-[var(--ui-quiet)]">{label}</span>
      <span className="font-semibold">{value}</span>
      {check ? <Check aria-hidden strokeWidth={3} className="size-3 text-[var(--ui-success)]" /> : null}
    </span>
  );
}

/** The confidence as a ring filling to its score, the number counting with it. */
function Gauge({ run, size = 84 }: { run: boolean; size?: number }) {
  const value = useTicker(MATCH_CONFIDENCE, run, 1000);
  const radius = size / 2 - 5;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className={`${ui.panel} relative grid place-items-center rounded-full`} style={{ width: size, height: size, ...PILL }}>
      <svg aria-hidden width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--ui-line)" strokeWidth="3" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--ui-primary)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${(value / 100) * circumference} ${circumference}`}
        />
      </svg>
      <span className="flex flex-col items-center leading-none">
        <span style={DISPLAY} className="text-[20px] font-semibold tracking-[-0.02em] tabular-nums">
          {Math.round(value)}%
        </span>
        <span className="mt-1 text-[9.5px] tracking-[0.02em] text-[var(--ui-quiet)]">confidence</span>
      </span>
    </div>
  );
}

function LinkedCard() {
  return (
    <Panel lifted className="p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[12px] text-[var(--ui-quiet)]">
          <SourceMark source="xero" size={14} />
          <SourceMark source="hubspot" size={14} />
          <span>
            <span className="font-medium text-[var(--ui-ink)]">One customer</span> in Xero and HubSpot
          </span>
        </span>
        <span
          className="flex items-center gap-1 bg-[var(--ui-success-soft)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--ui-success)] shadow-[inset_0_0_0_1px_var(--ui-success-line)]"
          style={PILL}
        >
          <Link2 aria-hidden className="size-3" /> Linked
        </span>
      </div>
      <p style={DISPLAY} className="mt-2 text-[19px] leading-tight font-semibold tracking-[-0.015em]">
        {XERO.name}
      </p>
      <p className="mt-0.5 text-[12px] text-[var(--ui-quiet)]">Also {HUBSPOT.name}, in HubSpot</p>
      <dl className="mt-3.5 border-t border-[var(--ui-line)] pt-1">
        {LINKED_FIELDS.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-2 text-[13px]">
            <dt className="text-[var(--ui-quiet)]">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/** The evidence between the cards when they stand side by side: a thread on each matched field's line, its chip, then the confidence between them. */
function Threads({ step, x1, x2, nameY, siteY }: { step: number; x1: number; x2: number; nameY: number; siteY: number }) {
  const midX = (x1 + x2) / 2;
  const centred = (top: number): CSSProperties => ({ left: midX, top, transform: "translate(-50%, -50%)" });
  const threads = [
    { at: NAMES, y: nameY },
    { at: DOMAIN, y: siteY },
  ].filter((thread) => step >= thread.at);
  return (
    <div className="pointer-events-none absolute inset-0 hidden md:block" style={{ opacity: step >= CONVERGE ? 0 : 1, transition: "opacity 300ms" }}>
      <svg className="absolute inset-0 h-full w-full overflow-visible">
        {threads.map((thread) => (
          <g key={thread.at}>
            <path d={`M ${x1} ${thread.y} H ${x2}`} pathLength={1} className={ui.draw} fill="none" stroke="var(--ui-primary)" strokeOpacity={0.5} strokeWidth={1.5} />
            <circle cx={x1} cy={thread.y} r={3.5} fill="var(--ui-panel)" stroke="var(--ui-primary)" strokeWidth={1.5} />
            <circle cx={x2} cy={thread.y} r={3.5} fill="var(--ui-panel)" stroke="var(--ui-primary)" strokeWidth={1.5} />
          </g>
        ))}
      </svg>
      {step >= NAMES ? (
        <div className="absolute" style={centred(nameY)}>
          <EvidenceChip label="Names" value={`${NAME_SCORE}% alike`} style={{ animationDelay: "250ms" }} />
        </div>
      ) : null}
      {step >= DOMAIN ? (
        <div className="absolute" style={centred(siteY)}>
          <EvidenceChip label="Domain" value="identical" check style={{ animationDelay: "250ms" }} />
        </div>
      ) : null}
      {step >= SCORE ? (
        <div className="absolute" style={centred((nameY + siteY) / 2)}>
          <div className={ui.pop}>
            <Gauge run />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The Inbox match as motion (the home's, since 2026-10-01): the two records
 * arrive, the evidence draws between them, the confidence fills, a person
 * accepts, and one linked customer remains. Decoration to assistive
 * technology; the band that stages it prints MATCH_SUMMARY.
 */
export function MatchEvidence() {
  const step = useTimeline(STEPS, { restStep: LINKED });
  const stage = useRef<HTMLDivElement>(null);
  const anchors = useAnchors(stage);
  const left = anchors.left;
  const right = anchors.right;
  const accept = anchors.accept;
  const stacked = !left || !right || right.y > left.y + 8;

  // Threads between the cards, on the matched fields' lines (side by side only).
  const x1 = left ? left.x + left.width : 0;
  const x2 = right?.x ?? 0;
  const nameY = centre(anchors["left-name"]).y;
  const siteY = centre(anchors["left-site"]).y;

  // Converging: each card moves halfway to the other.
  const from = centre(left);
  const to = centre(right);
  const dx = (to.x - from.x) / 2;
  const dy = (to.y - from.y) / 2;
  const converge = (sign: 1 | -1): CSSProperties => ({
    transform: step >= CONVERGE ? `translate(${sign * dx}px, ${sign * dy}px) scale(0.97)` : "none",
    opacity: step >= LINKED ? 0 : 1,
    transition: step === 0 ? "none" : "transform 700ms var(--ui-ease), opacity 350ms var(--ui-ease)",
  });

  const start = accept ? { x: accept.x + accept.width + 150, y: accept.y + accept.height + 80 } : { x: 0, y: 0 };
  const target = centre(accept);
  const evidenceOn = step >= NAMES && step < CONVERGE;

  return (
    <UiRoot>
      <LoopStage out={step === OUT}>
        {/* data-nosnippet: the sample records never make a search result's snippet. */}
        <div ref={stage} aria-hidden="true" data-nosnippet="" className="relative mx-auto grid max-w-[64rem] gap-4 md:grid-cols-[minmax(0,1fr)_13rem_minmax(0,1fr)] md:gap-0">
          <div className="md:col-start-1 md:row-start-1" style={converge(1)}>
            <div style={enter(step >= CARDS)}>
              <RecordCard record={XERO} side="left" marks={{ name: evidenceOn, site: evidenceOn && step >= DOMAIN }} />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 md:hidden" style={{ opacity: step >= CONVERGE ? 0 : 1, transition: "opacity 300ms" }}>
            {step >= NAMES ? <EvidenceChip label="Names" value={`${NAME_SCORE}% alike`} /> : null}
            {step >= DOMAIN ? <EvidenceChip label="Domain" value="identical" check /> : null}
            {step >= SCORE ? <Gauge run size={72} /> : null}
          </div>
          <div className="md:col-start-3 md:row-start-1" style={converge(-1)}>
            <div style={enter(step >= CARDS, { delay: 120 })}>
              <RecordCard record={HUBSPOT} side="right" marks={{ name: evidenceOn, site: evidenceOn && step >= DOMAIN }} />
            </div>
          </div>

          {!stacked ? <Threads step={step} x1={x1} x2={x2} nameY={nameY} siteY={siteY} /> : null}

          {step >= CONVERGE && left ? (
            <div className="absolute" style={{ left: (from.x + to.x) / 2, top: (from.y + to.y) / 2, width: left.width, transform: "translate(-50%, -50%)" }}>
              <div style={{ ...enter(step >= LINKED, { y: 0, blur: 10, duration: 600 }), scale: step >= LINKED ? "1" : "0.96", transition: "opacity 600ms var(--ui-ease), filter 600ms var(--ui-ease), scale 700ms var(--ui-spring)" }}>
                <LinkedCard />
              </div>
            </div>
          ) : null}

          {/* A person's call, then what was chosen and the way back. */}
          <Decision
            question="Same customer?"
            shown={step >= ACTIONS}
            pressed={step === PRESS}
            made={step >= LINKED}
            after={<span className="text-[var(--ui-quiet)] underline decoration-[var(--ui-line-strong)] underline-offset-2">Undo</span>}
            className="mt-6 md:col-span-3 md:row-start-2 md:mt-8"
          />

          <Cursor at={step >= ACTIONS ? target : start} down={step === PRESS} shown={step >= ACTIONS && step < LINKED} />
        </div>
      </LoopStage>
    </UiRoot>
  );
}
